import { CheckCircleOutlined, CloseCircleOutlined } from "@ant-design/icons";
import React from "react";
import FanStateDTO from "src/models/FanStateDTO";
import SensorReading from "./SensorReading";

interface FanStateViewProps {
    fanStates: FanStateDTO[] | null | undefined;
}

const OnOff: React.FC<{ value: boolean | null | undefined }> = ({ value }) => {
    if (value == null) return <span style={{ color: '#aaa' }}>--</span>;
    return value
        ? <CheckCircleOutlined style={{ color: '#52c41a' }} />
        : <CloseCircleOutlined style={{ color: '#ff4d4f' }} />;
};

const fmt = (value: number | null | undefined, digits = 1, suffix = ''): string =>
    value == null ? '--' : `${value.toFixed(digits)}${suffix}`;

const fmtA = (mA: number | null | undefined): string =>
    mA == null ? '--' : `${(mA / 1000).toFixed(2)} A`;

const FanStateView: React.FC<FanStateViewProps> = ({ fanStates }) => {
    const fans = fanStates ?? [];
    const anyHeater = fans.some(f => f.hasHeater);

    const textColor = '#000';
    const borderColor = '#d9d9d9';

    const th: React.CSSProperties = {
        textAlign: 'center',
        fontWeight: 600,
        padding: '4px 10px',
        borderBottom: `1px solid ${borderColor}`,
        fontSize: 12,
        color: textColor,
    };
    const label: React.CSSProperties = {
        textAlign: 'left',
        fontWeight: 500,
        padding: '4px 8px',
        fontSize: 12,
        color: textColor,
        whiteSpace: 'nowrap',
    };
    const cell: React.CSSProperties = {
        textAlign: 'center',
        padding: '4px 10px',
        fontFamily: 'monospace',
        fontSize: 12,
        color: textColor,
    };

    if (fans.length === 0) {
        return <span style={{ color: '#aaa', fontSize: 12 }}>No fan data</span>;
    }

    return (
        <div style={{ overflowX: 'auto' }}>
            <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                <thead>
                    <tr>
                        <th style={{ ...th, textAlign: 'left' }}></th>
                        {fans.map(f => (
                            <th key={f.number} style={th}>Fan {f.number}</th>
                        ))}
                    </tr>
                </thead>
                <tbody>
                    <tr>
                        <td style={label}>Fan</td>
                        {fans.map(f => (
                            <td key={f.number} style={cell}><OnOff value={f.isFanOn} /></td>
                        ))}
                    </tr>
                    <tr>
                        <td style={label}>Current</td>
                        {fans.map(f => (
                            <td key={f.number} style={cell}>
                                <SensorReading
                                    value={fmtA(f.fanCurrentSensorState?.currentMilliAmps)}
                                    code={f.fanCurrentSensorState?.statusCodeRaw}
                                />
                            </td>
                        ))}
                    </tr>

                    {anyHeater && <>
                        <tr>
                            <td style={label}>Heater</td>
                            {fans.map(f => (
                                <td key={f.number} style={cell}>
                                    {f.hasHeater ? <OnOff value={f.isHeaterOn} /> : <span style={{ color: '#aaa' }}>N/A</span>}
                                </td>
                            ))}
                        </tr>
                        <tr>
                            <td style={label}>Hi-Fire A</td>
                            {fans.map(f => (
                                <td key={f.number} style={cell}>
                                    {f.hasHeater
                                        ? <SensorReading value={fmtA(f.highFireCurrentMilliAmps)} code={f.heaterCurrentSensorState?.statusCodeRaw} />
                                        : <span style={{ color: '#aaa' }}>N/A</span>}
                                </td>
                            ))}
                        </tr>
                        <tr>
                            <td style={label}>Lo-Fire A</td>
                            {fans.map(f => (
                                <td key={f.number} style={cell}>
                                    {f.hasHeater
                                        ? <SensorReading value={fmtA(f.lowFireCurrentMilliAmps)} code={f.heaterCurrentSensorState?.statusCodeRaw} />
                                        : <span style={{ color: '#aaa' }}>N/A</span>}
                                </td>
                            ))}
                        </tr>
                    </>}

                    <tr>
                        <td style={label}>Plenum Temp</td>
                        {fans.map(f => (
                            <td key={f.number} style={cell}>
                                <SensorReading
                                    value={fmt(f.plenumRhtSensorState?.temperatureF, 1, '°F')}
                                    code={f.plenumRhtSensorState?.statusCodeRaw}
                                />
                            </td>
                        ))}
                    </tr>
                    <tr>
                        <td style={label}>Plenum RH</td>
                        {fans.map(f => (
                            <td key={f.number} style={cell}>
                                <SensorReading
                                    value={fmt(f.plenumRhtSensorState?.rhPercent, 0, '%')}
                                    code={f.plenumRhtSensorState?.statusCodeRaw}
                                />
                            </td>
                        ))}
                    </tr>
                    <tr>
                        <td style={label}>Pressure</td>
                        {fans.map(f => (
                            <td key={f.number} style={cell}>
                                <SensorReading
                                    value={fmt(f.plenumPressureSensorState?.pressurePsi, 2, ' PSI')}
                                    code={f.plenumPressureSensorState?.statusCodeRaw}
                                />
                            </td>
                        ))}
                    </tr>
                    <tr>
                        <td style={label}>Ambient Temp</td>
                        {fans.map(f => (
                            <td key={f.number} style={cell}>
                                <SensorReading
                                    value={fmt(f.ambientRhtSensorState?.temperatureF, 1, '°F')}
                                    code={f.ambientRhtSensorState?.statusCodeRaw}
                                />
                            </td>
                        ))}
                    </tr>
                    <tr>
                        <td style={label}>Ambient RH</td>
                        {fans.map(f => (
                            <td key={f.number} style={cell}>
                                <SensorReading
                                    value={fmt(f.ambientRhtSensorState?.rhPercent, 0, '%')}
                                    code={f.ambientRhtSensorState?.statusCodeRaw}
                                />
                            </td>
                        ))}
                    </tr>
                </tbody>
            </table>
        </div>
    );
};

export default FanStateView;
